import React, { memo, useCallback, useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { Alert, Button, ModalTrigger, SideBarToModal, SubHeading } from 'reaxl';
import { Form, Input, Select, StandardEmailInput, StandardPhoneInput, Textarea, validationRules } from 'reaxl-forms';

validationRules();

const options = {
    initial: [
        { label: 'My issue is', value: '' },
        { label: 'Pricing Issue', value: 'Pricing Issue' },
        { label: 'I Suspect a Scam', value: 'I Suspect a Scam' },
        { label: 'Inaccurate Information', value: 'Inaccurate Information' },
        { label: 'Received a Fraud/Spam Email', value: 'Received a Fraud/Spam Email' },
        { label: 'Issues with Seller', value: 'Issues with Seller' },
    ],
    'Pricing Issue': [
        { label: 'My Issue Involves', value: '' },
        { label: 'Price Too High', value: 'Price Too High_2' },
        { label: 'Price Too Low', value: 'Price Too Low_3' },
        { label: 'Payment Listed as Price', value: 'Payment Listed as Price_3' },
        { label: 'Other', value: 'Other_8' },
    ],
    'I Suspect a Scam': [
        { label: 'My Issue Involves', value: '' },
        { label: 'Bait & Switch', value: 'Bait & Switch_1' },
        { label: 'Vehicle Doesn’t Exist', value: 'Vehicle Doesn’t Exist_1' },
        { label: 'Request Deposit/Down Payment', value: 'Request Deposit/Down Payment_4' },
        { label: 'Unauthorized Charge', value: 'Unauthorized Charge_1' },
        { label: 'Misleading Content', value: 'Misleading Content_1' },
        { label: 'Other', value: 'Other_1' },
    ],
    'Inaccurate Information': [
        { label: 'My Issue Involves', value: '' },
        { label: 'Pictures Don’t Match', value: 'Pictures Don’t Match_5' },
        { label: 'Wrong Trim', value: 'Wrong Trim_5' },
        { label: 'Wrong Mileage', value: 'Wrong Mileage_5' },
        { label: 'Vehicle Options Inaccurate', value: 'Vehicle Options Inaccurate_5' },
        { label: 'Salvage History Not Disclosed', value: 'Salvage History Not Disclosed_5' },
        { label: 'Other', value: 'Other_5' },
    ],
    'Received a Fraud/Spam Email': [
        { label: 'My Issue Involves', value: '' },
        { label: 'Private Seller', value: 'Private Seller_1' },
        { label: 'Dealers Spamming Me', value: 'Dealers Spamming Me_1' },
        { label: 'Other', value: 'Other_1' },
    ],
    'Issues with Seller': [
        { label: 'My Issue Involves', value: '' },
        { label: 'Unresponsive Seller', value: 'Unresponsive Seller_8' },
        { label: 'Other', value: 'Other_8' },
    ],
    initSubType: [
        { label: 'My Issue Involves', value: '' },
    ],
};
const validationMessages = {
    hasInvalidChars: 'contains invalid characters.',
    enterFirst: 'Please enter your first name.',
    enterLast: 'Please enter your last name.',
    isProfane: 'Please remove all profanity from your message.',
};
const nameValidation = {
    isValidName: true,
    isNotProfane: true,
};

const firstNameValidationErrors = {
    isValidName: `First Name ${validationMessages.hasInvalidChars}`,
    isDefaultRequiredValue: validationMessages.enterFirst,
    isNotProfane: validationMessages.isProfane,
};
const lastNameValidationErrors = {
    isValidName: `Last Name ${validationMessages.hasInvalidChars}`,
    isNotProfane: validationMessages.isProfane,
};

const renderError = (message) => <Alert bsStyle="danger">{message}</Alert>;

const confirmation = (
    <div className="margin-4">
        Thank you for reporting this issue. We&apos;ve sent it to our Fraud Department.
    </div>
);

function FraudReportForm({
    listing = {},
    onSubmit,
    serverError,
    isXs,
}) {
    const [fraudType, updateFraudType] = useState('');
    const [showCallSection, toggleCallSection] = useState(false);

    useEffect(() => {
        const isPrivateSeller = _get(listing, 'owner.privateSeller', false);
        if (isPrivateSeller) {
            options['Issues with Seller'].splice(1, 0, { label: 'Dealer Listed as Private Seller', value: 'Dealer Listed as Private Seller_8' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const subTypeOptions = fraudType ? options[fraudType] : options.initSubType;

    const handleFormSubmit = useCallback((formData) => (onSubmit({ ...listing, ...formData })), [onSubmit, listing]);

    const onFraudTypeSelection = useCallback((event) => {
        if (event.target.value !== fraudType) {
            updateFraudType(event.target.value);
        }
        if (showCallSection && event.target.value !== 'I Suspect a Scam') {
            toggleCallSection(false);
        }
    }, [fraudType, showCallSection]);

    const onSubTypeSelect = useCallback((event) => {
        if (event.target.value === 'Unauthorized Charge_1') {
            toggleCallSection(true);
        } else {
            toggleCallSection(false);
        }
    }, [toggleCallSection]);

    return (
        <Form onValidSubmit={handleFormSubmit}>
            {serverError && renderError(serverError)}

            <Select
                name="fraudType"
                key="fraudType"
                options={options.initial}
                validationErrors={{
                    isDefaultRequiredValue: 'Select a Fraud Type',
                }}
                onChange={onFraudTypeSelection}
                required
            />
            <Select
                name="fraudSubType"
                key="fraudSubType"
                options={subTypeOptions}
                validationErrors={{
                    isDefaultRequiredValue: 'Select a Fraud Sub Type',
                }}
                value={fraudType}
                onChange={onSubTypeSelect}
                disabled={!fraudType}
                required
            />

            {!showCallSection && (
                <Textarea
                    name="explanation"
                    placeholder="Tell us about your issue in detail (Optional). Maximum 3500 characters."
                    maxLength="3500"
                />
            )}

            {showCallSection && (
                <>
                    <Alert
                        bsStyle="danger"
                        className="margin-bottom-3"
                    >
                        Please call
                        <b> 877-742-8040 </b>
                        to speak with a Fraud Watch Customer Service Representative directly.
                    </Alert>

                    {isXs && (
                        <Button
                            className="padding-horizontal-6 margin-bottom-3"
                            bsStyle="primary"
                            href="tel:+1877-742-8040"
                            rel="nofollow"
                        >
                            Call Now
                        </Button>
                    )}

                    <SubHeading
                        size={400}
                        className="margin-top-4 margin-bottom-2"
                    >
                        Or, if you&apos;d prefer, we can call you instead.
                    </SubHeading>
                </>
            )}

            <Input
                maxLength="20"
                name="firstName"
                placeholder="First Name"
                validations={nameValidation}
                validationErrors={firstNameValidationErrors}
                required
                value=""
            />
            <Input
                maxLength="20"
                name="lastName"
                value=""
                placeholder="Last Name (Optional)"
                validations={nameValidation}
                validationErrors={lastNameValidationErrors}
            />

            {!showCallSection && (
                <StandardEmailInput
                    name="email"
                    placeholder="Email (Optional)"
                    validateOn="change"
                    label="Enter your email address if you&apos;d like to be contacted about this report."
                />
            )}

            {showCallSection && (
                <StandardPhoneInput
                    label=""
                    name="phoneNumber"
                    placeholder="Phone Number"
                    required
                />
            )}

            <Button
                className="padding-horizontal-6"
                bsStyle={showCallSection ? 'secondary' : 'primary'}
                type="submit"
            >
                {showCallSection ? 'Request a Call' : 'Send Feedback'}
            </Button>
        </Form>
    );
}

/**
 *  FraudReport displays a trigger and will open a Fraud Report modal
 *
 */
function FraudReport({
    listing = {},
    onSubmit,
    onClose,
    serverError,
    showConfirmation,
    className,
    triggerTextRef,
    ...rest
}) {
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const rootClassName = clsx(
        'text-link',
        className
    );

    return (
        <span
            data-cmp="fraudReport"
            className={rootClassName}
            {...rest}
        >
            <ModalTrigger
                modal={(
                    <SideBarToModal
                        title="Report Fraud or Inaccurate Vehicle Info"
                        className="display-inline-block"
                        content={
                            showConfirmation ? confirmation
                                : (
                                    <FraudReportForm
                                        listing={listing}
                                        onSubmit={onSubmit}
                                        serverError={serverError}
                                        isXs={isXs}
                                    />
                                )
                        }
                        collapsed={isXs}
                        onHide={onClose}
                        sideBarOptions={{
                            fullWidth: true,
                        }}
                        modalOptions={{
                            bsSize: 'small',
                            scrollPanel: false,
                        }}
                    />
                )}
            >
                <span ref={triggerTextRef}>
                    Let us know
                </span>
            </ModalTrigger>
        </span>
    );
}

FraudReport.propTypes = {
    /**
     * The className to apply to the root node
     */
    className: PropTypes.string,
    /**
     * The listing object is a group of attributes to pass when submit fraud report.
     */
    listing: PropTypes.object,
    /**
     * The onSubmit function callback handles the submitting of the form data
     */
    onSubmit: PropTypes.func.isRequired,
    /**
     * The callback function to handle the closing of the FraudReport modal.
     */
    onClose: PropTypes.func,
    /**
     * The serverError string represents a server side error.
     */
    serverError: PropTypes.string,
    /**
     * If true, the confirmation will render in place of the form.
     */
    showConfirmation: PropTypes.bool,
};

export default memo(FraudReport);
